/* eslint-disable */
import {Observable, Observer} from 'rxjs';
import {Injectable} from '@angular/core';
import {PagingRequest, QueryResult, ResourceQueryResult, Services} from '../../util/services';
import {List, Map as ImmutableMap, Set} from 'immutable';
import {DqlQuery, Query} from '../../query/field';
import {FilterField} from '../../query/filterfields';
import {OrderField} from '../../query/orderfields';
import {OrderFunctionResolver} from '../../../util/core-utils';
import {IdentityMessage} from '../../util/messages';
import {OutgoingSmsResource, TaskRecordOutgoingSmsResourceService} from "./task-record-outgoing-sms-resource.service";
import {PhoneNumber, PhoneNumbers} from "../../util/phone-number";
import {SmsTemplate} from "../sms-template/sms-template.service";
import {OffsetDateTime} from "../../util/dates";

/* eslint-enable */

@Injectable()
export class TaskRecordOutgoingSmsService implements OutgoingSms.Service {

  private readonly filterField = new FilterField.OutgoingSms();
  private readonly orderField = new OrderField.OutgoingSms();
  private readonly fields = new OutgoingSms.Fields.OutgoingSms();

  constructor(private taskRecordOutgoingSmsResourceService: TaskRecordOutgoingSmsResourceService) {
  }

  query(request: OutgoingSms.QueryRequest): Observable<QueryResult<OutgoingSms.OutgoingSms>> {
    return Observable.create((observer: Observer<QueryResult<OutgoingSms.OutgoingSms>>) => {
      const resourceRequest: OutgoingSmsResource.TaskRecordQueryRequest = this.toResourceQueryRequest(request);
      return this.taskRecordOutgoingSmsResourceService.query(resourceRequest).subscribe(
        (result: ResourceQueryResult<OutgoingSmsResource.OutgoingSms>) => {
          observer.next({
            items: List.of(...result.items.map((item) => OutgoingSms.toPublic(item))),
            pagingResult: result.pagingResult
          });
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  send(request: OutgoingSms.SendRequest): Observable<IdentityMessage> {
    return Observable.create((observer: Observer<IdentityMessage>) => {
      const resourceRequest: OutgoingSmsResource.SendRequest = this.toResourceSendRequest(request);
      return this.taskRecordOutgoingSmsResourceService.send(resourceRequest).subscribe(
        (result: IdentityMessage) => {
          observer.next(result);
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  toResourceQueryRequest(request: OutgoingSms.QueryRequest): OutgoingSmsResource.TaskRecordQueryRequest {
    const filter: string | undefined = DqlQuery.toOptionalFilter(this.filterField, request.filter);
    const order: string | undefined = DqlQuery.toOptionalOrder(this.orderField, request.order);
    const fields: string | undefined = DqlQuery.toOptionalFields(this.fields, request.fields);
    return {
      task_id: request.taskId,
      task_record_id: request.taskRecordId,
      filter: filter,
      order: order,
      fields: fields,
      page_number: request.paging ? request.paging.pageNumber : undefined,
      number_of_items: request.paging ? request.paging.numberOfItems : undefined,
      rights: DqlQuery.RightRequestSerializer.getInstance().serialize(request.rights)
    };
  }

  toResourceSendRequest(request: OutgoingSms.SendRequest): OutgoingSmsResource.SendRequest {
    return {
      task_id: request.taskId,
      task_record_id: request.taskRecordId,
      previous_sms_id: request.previousSmsId,
      phone_number: request.phoneNumber.toIso()!,
      message: request.message,
      integration_type: request.integrationType
    };
  }

}

export namespace OutgoingSms {

  import SmsIntegrationType = SmsTemplate.SmsIntegrationType;

  export interface Service {
    query(request: QueryRequest): Observable<QueryResult<OutgoingSms>>;

    send(request: SendRequest): Observable<IdentityMessage>;
  }

  export interface OutgoingSms {
    id?: number;
    content?: string;
    recipient?: PhoneNumber;
    state?: SmsState;
    integrationType?: SmsIntegrationType;
    sendMode?: SmsSendMode;
    deliveryTime?: OffsetDateTime;
    messageId?: string;
    statusCode?: number;
    statusInfo?: string;
    errorStatusCode?: string;
    responseReceived?: boolean;
    bipState?: BipSmsState;
    bipStateMessage?: string;
    bipStateTimestamp?: OffsetDateTime;
    resendCount?: number;
    taskRecords?: {
      id: number;
      taskId: number;
      name: string;
    }[];
    grantedRights: Set<string>;
  }

  export enum SmsState {
    WAITING = 'WAITING',
    REQUEST_SENT = 'REQUEST_SENT',
    SUCCESSFUL_DELIVERY = 'SUCCESSFUL_DELIVERY',
    REQUEST_FAILED = 'REQUEST_FAILED',
    FAILED_DELIVERY = 'FAILED_DELIVERY',
  }

  export enum SmsSendMode {
    TRIGGER = 'TRIGGER',
    MANUAL = 'MANUAL',
    RESEND = 'RESEND',
    RATING_REQUEST = 'RATING_REQUEST',
  }

  export enum BipSmsState {
    SENT = 'SENT',
    ACCEPTED = 'ACCEPTED',
    WAITING = 'WAITING',
    DELIVERED = 'DELIVERED',
    REJECTED = 'REJECTED',
    UNDELIVERABLE = 'UNDELIVERABLE',
    MISC = 'MISC',
    STOPPED = 'STOPPED',
    EXPIRED = 'EXPIRED',
    STATUSLOST = 'STATUSLOST',
  }

  export const smsStates: { state: SmsState, stringKey: string }[] = [
    {state: SmsState.WAITING, stringKey: 'OUTGOING_SMS_STATE_WAITING'},
    {state: SmsState.REQUEST_SENT, stringKey: 'OUTGOING_SMS_STATE_REQUEST_SENT'},
    {state: SmsState.SUCCESSFUL_DELIVERY, stringKey: 'OUTGOING_SMS_STATE_SUCCESSFUL_DELIVERY'},
    {state: SmsState.REQUEST_FAILED, stringKey: 'OUTGOING_SMS_STATE_REQUEST_FAILED'},
    {state: SmsState.FAILED_DELIVERY, stringKey: 'OUTGOING_SMS_STATE_FAILED_DELIVERY'},
  ];

  export const smsSendModes: { sendMode: SmsSendMode, stringKey: string }[] = [
    {sendMode: SmsSendMode.TRIGGER, stringKey: 'OUTGOING_SMS_SEND_MODE_TRIGGER'},
    {sendMode: SmsSendMode.MANUAL, stringKey: 'OUTGOING_SMS_SEND_MODE_MANUAL'},
    {sendMode: SmsSendMode.RESEND, stringKey: 'OUTGOING_SMS_SEND_MODE_RESEND'},
    {sendMode: SmsSendMode.RATING_REQUEST, stringKey: 'OUTGOING_SMS_SEND_MODE_RATING_REQUEST'},
  ];

  export interface QueryRequest {
    taskId: number;
    taskRecordId: number;
    fields?: Query.FieldFunction<Fields.OutgoingSms>;
    filter?: Query.FilterFunction<FilterField.OutgoingSms>;
    order?: Query.OrderFunction<OrderField.OutgoingSms>;
    paging?: PagingRequest;
    rights?: Set<string>;
  }

  export interface SendRequest {
    taskId: number;
    taskRecordId: number;
    previousSmsId?: number;
    phoneNumber: PhoneNumber;
    message: string;
    integrationType: SmsIntegrationType;
  }

  export function toPublic(r: OutgoingSmsResource.OutgoingSms): OutgoingSms {
    return {
      id: r.id,
      content: r.content,
      recipient: PhoneNumbers.parse(r.recipient),
      state: <SmsState>r.state,
      integrationType: <SmsIntegrationType>r.integration_type,
      sendMode: <SmsSendMode>r.send_mode,
      deliveryTime: Services.toOffsetDateTime(r.delivery_time),
      messageId: r.message_id,
      statusCode: r.status_code,
      statusInfo: r.status_info,
      errorStatusCode: r.error_status_code,
      bipState: <BipSmsState>r.bip_state,
      bipStateMessage: r.bip_state_message,
      bipStateTimestamp: Services.toOffsetDateTime(r.bip_state_timestamp),
      resendCount: r.resend_count,
      taskRecords: r.task_records ? r.task_records.map(t => ({
        id: t.id,
        taskId: t.task_id,
        name: t.name,
      })) : undefined,
      grantedRights: r.granted_rights ? Set.of(...r.granted_rights) : Set.of(),
    }
  }

  export enum ValidatedField {
    UNKNOWN,
    PHONE_NUMBER,
    MESSAGE,
    INTEGRATION_TYPE,
  }

  export namespace Fields {

    export class OutgoingSms {

      readonly id: Query.Field = new DqlQuery.Field('id');
      readonly content: Query.Field = new DqlQuery.Field('content');
      readonly recipient: Query.Field = new DqlQuery.Field('recipient');
      readonly state: Query.Field = new DqlQuery.Field('state');
      readonly integrationType: Query.Field = new DqlQuery.Field('integration_type');
      readonly sendMode: Query.Field = new DqlQuery.Field('send_mode');
      readonly deliveryTime: Query.Field = new DqlQuery.Field('delivery_time');
      readonly messageId: Query.Field = new DqlQuery.Field('message_id');
      readonly statusCode: Query.Field = new DqlQuery.Field('status_code');
      readonly statusInfo: Query.Field = new DqlQuery.Field('status_info');
      readonly errorStatusCode: Query.Field = new DqlQuery.Field('error_status_code');
      readonly responseReceived: Query.Field = new DqlQuery.Field('response_received');
      readonly bipState: Query.Field = new DqlQuery.Field('bip_state');
      readonly bipStateMessage: Query.Field = new DqlQuery.Field('bip_state_message');
      readonly bipStateTimestamp: Query.Field = new DqlQuery.Field('bip_state_timestamp');
      readonly resendCount: Query.Field = new DqlQuery.Field('resend_count');
      readonly taskRecords: Query.Field = new DqlQuery.Field('task_records');

      get each(): Set<Query.Field> {
        return Set.of(
          this.id,
          this.content,
          this.recipient,
          this.state,
          this.integrationType,
          this.sendMode,
          this.deliveryTime,
          this.messageId,
          this.statusCode,
          this.statusInfo,
          this.errorStatusCode,
          this.responseReceived,
          this.bipState,
          this.bipStateMessage,
          this.bipStateTimestamp,
          this.resendCount,
          this.taskRecords,
        );
      }

      get taskRecordBasedList(): Set<Query.Field> {
        return Set.of(
          this.id,
          this.content,
          this.recipient,
          this.state,
          this.integrationType,
          this.sendMode,
          this.deliveryTime,
          this.messageId,
          this.statusCode,
          this.statusInfo,
          this.errorStatusCode,
          this.responseReceived,
          this.bipState,
          this.bipStateMessage,
          this.bipStateTimestamp,
          this.resendCount,
        );
      }

      get processListEmbedded(): Set<Query.Field> {
        return Set.of(
          this.id,
          this.state,
          this.messageId,
          this.deliveryTime,
          this.errorStatusCode,
          this.bipState,
          this.bipStateMessage,
          this.bipStateTimestamp,
        );
      }

      static get processListEmbedded(): string {
        return 'id,' +
          'state,' +
          'message_id,' +
          'delivery_time,' +
          'error_status_code,' +
          'bip_state,' +
          'bip_state_message,' +
          'send_mode,' +
          'integration_type,' +
          'bip_state_timestamp';

      }
    }

  }

  export namespace OrderFunctions {
    export const ID = (f: OrderField.OutgoingSms) => f.id;
    export const DELIVERY_TIME = (f: OrderField.OutgoingSms) => f.deliveryTime;
    export const MESSAGE_ID = (f: OrderField.OutgoingSms) => f.messageId;

    export const VALUES = OrderFunctionResolver.builder<OrderField.OutgoingSms>()
      .add(ID, 'id')
      .add(DELIVERY_TIME, 'delivery_time')
      .add(MESSAGE_ID, 'message_id')
      .build();
  }
}

class Keys {

  private static readonly PHONE_NUMBER = 'phone_number';
  private static readonly MESSAGE = 'message';
  private static readonly INTEGRATION_TYPE = 'integration_type';

  private static readonly keyValidatedFieldMap: ImmutableMap<string, OutgoingSms.ValidatedField> = ImmutableMap.of(
    Keys.PHONE_NUMBER, OutgoingSms.ValidatedField.PHONE_NUMBER,
    Keys.MESSAGE, OutgoingSms.ValidatedField.MESSAGE,
    Keys.INTEGRATION_TYPE, OutgoingSms.ValidatedField.INTEGRATION_TYPE,
  );

  public static toValidatedField(fieldKey: string): OutgoingSms.ValidatedField {
    return Keys.keyValidatedFieldMap.get(fieldKey, OutgoingSms.ValidatedField.UNKNOWN);
  }

}

